import { createContext } from 'svelte';

import type { PersonaSelectionView, PersonaView } from '#src/generated/protocol';

import type { CoreClient } from '#lib/core/client.svelte.js';

import { reorderPersonas, type PersonaAssociation } from './persona.js';

export class PersonaStore {
  personas = $state.raw<PersonaView[]>([]);
  account = $state<PersonaSelectionView | null>(null);
  rooms = $state<Record<string, PersonaSelectionView>>({});
  disabledRooms = $state.raw<string[]>([]);
  loading = $state(false);
  error = $state<string | null>(null);

  private loaded = false;
  private inFlight: Promise<void> | null = null;
  private revision: number | null = null;

  constructor(private readonly core: CoreClient) {}

  async load(force = false): Promise<void> {
    if (this.revision !== this.core.accountRevision) this.reset();
    if (this.loaded && !force) return;
    this.inFlight ??= this.fetch();
    await this.inFlight;
  }

  private reset(): void {
    this.revision = this.core.accountRevision;
    this.personas = [];
    this.account = null;
    this.rooms = {};
    this.disabledRooms = [];
    this.error = null;
    this.loaded = false;
    this.inFlight = null;
  }

  private async fetch(): Promise<void> {
    const revision = this.revision;
    this.loading = true;
    try {
      const catalog = await this.core.rpc.personas.list();
      if (revision !== this.revision) return;
      this.personas = catalog.personas;
      this.account = catalog.account;
      this.rooms = catalog.rooms;
      this.disabledRooms = catalog.disabled_rooms;
      this.loaded = true;
      this.error = null;
    } catch (cause) {
      if (revision !== this.revision) return;
      console.warn('[sable personas] loading the catalog failed', cause);
      this.error = 'personas.loadFailed';
    } finally {
      if (revision === this.revision) {
        this.loading = false;
        this.inFlight = null;
      }
    }
  }

  async save(persona: PersonaView, previousId: string | null = null): Promise<void> {
    this.personas = await this.core.rpc.personas.save({
      persona: $state.snapshot(persona),
      previous_id: previousId,
    });
    if (previousId !== null && previousId !== persona.id) this.repoint(previousId, persona.id);
  }

  async remove(id: string): Promise<void> {
    this.personas = await this.core.rpc.personas.remove({ id });
    this.repoint(id, null);
  }

  async removeAll(): Promise<void> {
    for (const { id } of this.personas) await this.remove(id);
  }

  async reorder(from: number, to: number): Promise<void> {
    if (to < 0 || to >= this.personas.length || from === to) return;
    const ids = reorderPersonas(this.personas, from, to).map((persona) => persona.id);
    this.personas = await this.core.rpc.personas.reorder({ ids });
  }

  selectionFor(roomId: string | null): PersonaSelectionView | null {
    return roomId === null ? this.account : (this.rooms[roomId] ?? null);
  }

  associationFor(roomId: string): PersonaAssociation {
    return this.disabledIn(roomId) ? false : (this.rooms[roomId] ?? undefined);
  }

  disabledIn(roomId: string): boolean {
    return this.disabledRooms.includes(roomId);
  }

  async disable(roomId: string): Promise<void> {
    await this.core.rpc.personas.disableRoom({ room_id: roomId });
    this.rooms = Object.fromEntries(Object.entries(this.rooms).filter(([key]) => key !== roomId));
    if (!this.disabledIn(roomId)) this.disabledRooms = [...this.disabledRooms, roomId];
  }

  async select(
    roomId: string | null,
    personaId: string | null,
    validUntil: number | null = null,
    alternateAvatarId: string | null = null
  ): Promise<void> {
    await this.core.rpc.personas.setSelection({
      room_id: roomId,
      persona_id: personaId,
      valid_until: validUntil,
      alternate_avatar_id: alternateAvatarId,
    });
    const selection =
      personaId === null
        ? null
        : {
            persona_id: personaId,
            valid_until: validUntil,
            alternate_avatar_id: alternateAvatarId,
          };

    if (roomId === null) {
      this.account = selection;
      return;
    }
    this.disabledRooms = this.disabledRooms.filter((key) => key !== roomId);
    const rest = Object.fromEntries(Object.entries(this.rooms).filter(([key]) => key !== roomId));
    this.rooms = selection === null ? rest : { ...rest, [roomId]: selection };
  }

  private repoint(from: string, to: string | null): void {
    if (this.account?.persona_id === from) {
      this.account = to === null ? null : { ...this.account, persona_id: to };
    }

    this.rooms = Object.fromEntries(
      Object.entries(this.rooms).flatMap(([roomId, selection]) => {
        if (selection.persona_id !== from) return [[roomId, selection]];
        return to === null ? [] : [[roomId, { ...selection, persona_id: to }]];
      })
    );
  }
}

export const [usePersonaStore, providePersonaStore] = createContext<PersonaStore>();
